import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lock, Mail, Eye, EyeOff, AlertCircle, X, Send, CheckCircle, User, Phone, Globe, MessageSquare } from "lucide-react";
import axios from "axios";
import { getDeviceFingerprint } from "@/lib/fingerprint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "United States", "United Kingdom", "Canada", "Australia", "Germany", "France", 
  "Spain", "Italy", "Netherlands", "Belgium", "Switzerland", "Austria",
  "Saudi Arabia", "UAE", "Qatar", "Kuwait", "Bahrain", "Oman", "Jordan", "Lebanon",
  "Egypt", "Morocco", "Algeria", "Tunisia", "Iraq", "Syria", "Palestine",
  "India", "Pakistan", "Bangladesh", "Indonesia", "Malaysia", "Singapore",
  "Japan", "South Korea", "China", "Hong Kong", "Taiwan", "Thailand", "Vietnam",
  "Brazil", "Mexico", "Argentina", "Colombia", "Chile", "Peru",
  "South Africa", "Nigeria", "Kenya", "Ghana",
  "Russia", "Ukraine", "Poland", "Czech Republic", "Romania", "Hungary",
  "Turkey", "Israel", "Iran", "Other"
];

export function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Trigger health check on mount - seeds DB if empty (fixes production login)
  useEffect(() => {
    axios.get(`${API}/health`).catch(() => {});
  }, []);
  
  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    request: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const fingerprint = await getDeviceFingerprint().catch(() => null);
      const res = await axios.post(`${API}/auth/login`, {
        email,
        password,
        fingerprint,
        user_agent: navigator.userAgent,
      });
      if (res.data.access_token) {
        localStorage.setItem("token", res.data.access_token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        onLogin(res.data.user, res.data.access_token);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "device_mismatch") {
        setError("DEVICE_MISMATCH");
      } else {
        setError(typeof detail === "string" ? detail : "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError("");
    setContactLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      setContactForm({ name: "", email: "", phone: "", country: "", request: "" });
    } catch (err) {
      setContactError(err.response?.data?.detail || "Failed to send request");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src="/logo.png" alt="TTANALYSIS" className="w-20 h-20" />
          </div>
          <div>
            <CardTitle className="text-2xl text-white">TTANALYSIS</CardTitle>
            <CardDescription className="text-zinc-400 mt-2">
              Smart Trading Analysis Platform
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                {error === "DEVICE_MISMATCH" ? (
                  <div className="text-sm space-y-2">
                    <p className="font-bold">⛔ Login Denied / تم رفض تسجيل الدخول</p>
                    <p>This account is bound to another device. You cannot log in from this device.</p>
                    <p>If this is your primary device and you lost access to the previous one, contact admin to reset the device binding.</p>
                    <hr className="border-red-500/30" />
                    <p dir="rtl">هذا الحساب مرتبط بجهاز آخر. لا يمكنك تسجيل الدخول من هذا الجهاز.</p>
                    <p dir="rtl">إذا كان هذا جهازك الأساسي وفقدت الوصول للجهاز السابق، تواصل مع الإدارة لإعادة تعيين الجهاز.</p>
                  </div>
                ) : (
                  <span className="text-sm">{error}</span>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Loading..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 text-center mb-3">
              Don't have an account?
            </p>
            <Button
              variant="outline"
              className="w-full border-purple-500 text-purple-400 hover:bg-purple-500/20"
              onClick={() => setShowContactForm(true)}
              data-testid="contact-us-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-zinc-900 border-zinc-700 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-zinc-900 z-10">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  Contact Us
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Fill out the form to request an account
                </CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => {
                  setShowContactForm(false);
                  setContactSuccess(false);
                  setContactError("");
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent>
              {contactSuccess ? (
                <div className="text-center py-8">
                  <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Request Sent!</h3>
                  <p className="text-zinc-400 mb-6">
                    Thank you for your interest. We will review your request and get back to you soon.
                  </p>
                  <Button 
                    onClick={() => {
                      setShowContactForm(false);
                      setContactSuccess(false);
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {contactError && (
                    <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm">{contactError}</span>
                    </div>
                  )}

                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <User className="w-4 h-4 inline mr-1" /> Full Name *
                    </label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Your full name"
                      required
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <Mail className="w-4 h-4 inline mr-1" /> Email *
                    </label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="your@email.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <Phone className="w-4 h-4 inline mr-1" /> Phone Number *
                    </label>
                    <Input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="+1234567890"
                      required
                      data-testid="contact-phone"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <Globe className="w-4 h-4 inline mr-1" /> Country *
                    </label>
                    <Select
                      value={contactForm.country}
                      onValueChange={(v) => setContactForm({ ...contactForm, country: v })}
                      required
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="contact-country">
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-zinc-400 block mb-1">
                      <MessageSquare className="w-4 h-4 inline mr-1" /> Your Request *
                    </label>
                    <Textarea
                      value={contactForm.request}
                      onChange={(e) => setContactForm({ ...contactForm, request: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 min-h-[100px]"
                      placeholder="Tell us what you need... (e.g., I want access to analyze forex signals for my trading)"
                      required
                      data-testid="contact-request"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    disabled={contactLoading}
                    data-testid="contact-submit"
                  >
                    {contactLoading ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Send Request
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
